/**
 * P36 (Feb 28, 2026) — Unified Billing Hub.
 *
 * Single landing page for the Billing section. Replaces the cluttered
 * 6-item sidebar group with:
 *   - Prominent "New Claim" CTA at the top
 *   - Quick-stats row (Drafts, Pending, Awaiting, Denials, Revenue MTD)
 *   - Card grid for Claims / Denials / ODM / BH / DODD
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { billingApi } from '../services/api';
import HubStatsRow from './billing-hub/HubStatsRow';
import HubSectionCards from './billing-hub/HubSectionCards';

const BillingHubPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await billingApi.getHubStats();
        if (mounted) setStats(res.data);
      } catch {
        // Non-fatal — tiles fall back to '—'.
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="billing-hub-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Billing
          </h1>
          <p className="text-slate-500 mt-1">
            Everything billing in one place — claims, denials, ODM, BH, DODD.
          </p>
        </div>
        <Button
          onClick={() => navigate('/claims/new')}
          className="bg-indigo-900 hover:bg-indigo-800 text-white px-6 py-6 text-base"
          data-testid="hub-new-claim-cta"
        >
          <Plus size={20} className="mr-2" weight="bold" />
          New Claim
        </Button>
      </div>

      {/* Quick-stats row */}
      <HubStatsRow stats={stats} loading={loading} />

      {/* Section cards */}
      <div className="pt-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-4">
          Choose what to do
        </h2>
        <HubSectionCards />
      </div>
    </div>
  );
};

export default BillingHubPage;
